import { DialogActions, DialogContent, FormHelperText, Grid, InputAdornment, TextField } from "@mui/material";
import { ColDef, GridApi, ICellRendererParams, ValueGetterParams } from "ag-grid-community";
import { useEffect, useMemo, useState } from "react";
import setToast from "../../helpers/toaster.helper";
import { Contact, Group } from "../../models/conversations";
import { GroupContactService } from "../../services/GroupContactService";
import { UtilityService } from "../../services/UtilityService";
import ButtonComponent from "../shared/button/button.component";
import { GridStatusLabelVariant, ToasterTexts, buttonLabels, emptyLabels } from "../shared/constants";
import { validateForm } from "../shared/form-validation";
import GridComponent from "../shared/grid";
import { StatusRenderer } from "../shared/grid/statusRenderer";
import { ToasterType } from "../shared/toaster/toaster.model";
import { uniqBy } from 'lodash';
import { useSelector } from "react-redux";
import { GridSearchIcon } from "../../icon";
import * as selector from '../../selectors/selectors';

interface IProps extends Group {
    closeHandler: (flag: boolean) => void
    IsMobileDevice: boolean;
    ContactsInGroup?: Contact[];
    clearSelection: () => void;
    checkedContacts?: Contact[];
}

const EditGroupComponent = (props: IProps) => {
    const Contacts: Contact[] = useSelector(selector.contacts);
    const IsMobileDevice: boolean = useSelector(selector.isMobileDevice);
    const IsDarkMode: boolean = useSelector(selector.isDarkMode);
    const IsTeamsApp: boolean = useSelector(selector.isTeamsApp);
    const [ContactsInGroup, setContactsInGroup] = useState<Contact[]>(props.ContactsInGroup);
    const [NewGroupName, setNewGroupName] = useState<string>(props.Name);
    const [IsLoading, setIsLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState({ NewGroupName: "", });
    const [gridApi, setGridApi] = useState<GridApi>(null);

    const editGroup = (name: any, value: any) => {
        validateForm({ [name]: value },
            (errors) => {
                setErrors({ NewGroupName: errors["NewGroupName"] })
            })
        setNewGroupName(value)
    }

    const EditGroupColoumnDefs: ColDef[] = [
        {
            field: "Name",
            headerName: "Name",
            headerCheckboxSelection: true,
            checkboxSelection: true,
            wrapText: true,
            autoHeight: true
        },
        {
            field: "IsBlocked" || "IsUnsubscribed",
            headerName: "Status",
            valueGetter: (params: ValueGetterParams) => {
                return params.data.IsUnsubscribed ? "Unsubscribed" : params.data.IsBlocked ? "Blocked" : "Active"
            },
            cellRendererSelector: (params: ICellRendererParams) => {
                if (params.data) {
                    const label = params.data.IsUnsubscribed ? "Unsubscribed" : params.data.IsBlocked ? "Blocked" : "Active";
                    const variant = params.data.IsUnsubscribed ? GridStatusLabelVariant.Warning : params.data.IsBlocked ? GridStatusLabelVariant.Danger : GridStatusLabelVariant.Active;
                    const Status = {
                        component: StatusRenderer,
                        params: {
                            Variant: variant, Label: label, Id: params.data.ID
                        }
                    }
                    return Status
                }
                return undefined
            },
        },
        {
            field: "MaskedPhoneNumber",
            headerName: "Phone Number",
            wrapText: true,
            autoHeight: true,
            valueFormatter: (params) => {
                return params.data.MaskedPhoneNumber
            },
            valueGetter: (params: ValueGetterParams) => {
                const raw =
                    params.data?.PhoneNumber ||
                    params.data?.MaskedPhoneNumber ||
                    "";
                return `${raw} ${UtilityService.maskPhoneNumberOnLength(raw)} ${raw.replace(/\D/g, "")}`;
            },
        },
    ]

    const searchContacts = (e: any) => {
        gridApi.setQuickFilter(e.target.value)
    }

    const saveGroup = (event: any) => {
        event.preventDefault();
        if (validateForm({ NewGroupName: NewGroupName },
            (errors) => {
                setErrors({ NewGroupName: errors["NewGroupName"] })
            })) {
            setIsLoading(true);
            let data = {
                id: props.ID,
                name: NewGroupName,
                count: ContactsInGroup?.length ?? 0,
                contacts: ContactsInGroup?.map(x => ({ contactId: x.ID })) ?? []
            };
            if (!props.ID) {
                GroupContactService.createGroup(data).then(
                    (res) => {
                        setIsLoading(false);
                        setContactsInGroup([]);
                        setNewGroupName("");
                        setToast(
                            ToasterTexts.GROUP_ADD_SUCCESS.replace(
                                "<Name>",
                                NewGroupName
                            ),
                            ToasterType.SUCCESS
                        );
                        props.clearSelection()
                        if (res.status === 200) {
                            props.closeHandler(true);
                        }
                    },
                    (error) => {
                        setIsLoading(false);
                        setToast(error.response.data.code === "MGC" ? ToasterTexts.GROUP_EDIT_FAILED_MAX_CONTACTS_EXCEEDED.replace("<count>", error.response.data.value) : error.response.data.message, ToasterType.DANGER);
                    }
                );
            } else {
                GroupContactService.updateGroup(data).then(res => {
                    setIsLoading(false);
                    setContactsInGroup([]);
                    if (res.status === 200) {
                        props.closeHandler(true);
                        setToast(ToasterTexts.GROUP_EDIT_SUCCESS.replace('<Name>', data.name), ToasterType.SUCCESS)
                    }
                    props.clearSelection()
                }, (error) => {
                    if (error.response.status === 400) {
                        setIsLoading(false);
                        setToast(error.response.data.code === "MGC" ? ToasterTexts.GROUP_EDIT_FAILED_MAX_CONTACTS_EXCEEDED.replace("<count>", error.response.data.value) : error.response.data.message, ToasterType.DANGER);
                    }
                })
            }
            props.closeHandler(true);
        }
    }

    const onGridReady = (params) => {
        setGridApi(params.api)
    }

    useEffect(() => {
        gridApi?.forEachNode(node => {
            node.setSelected(props.ContactsInGroup?.findIndex(x => x.ID === node.data.ID) >= 0)
        })
    }, [gridApi, props.ContactsInGroup, Contacts])

    const onSelectionChanged = () => {
        let SelectedContacts: Contact[] = gridApi.getSelectedRows()
        if (gridApi && SelectedContacts.length >= 0) {
            setContactsInGroup(SelectedContacts)
        }
        if (SelectedContacts?.length === 0 && !!data?.length) {
            if (!IsMobileDevice)
                setToast(ToasterTexts.GROUP_EDIT_CONTACTS_NULL, ToasterType.WARNING)
        }

    }

    const data = useMemo(() => uniqBy([...props.ContactsInGroup ?? [], ...Contacts], 'ID'), [Contacts, props.ContactsInGroup])

    return (
        <>
            <DialogContent className={` o-y-scroll pb-0 p-2 ${IsDarkMode ? "bg-color-sidebar-bg" : ""} `}>
                <form className={`${props.IsMobileDevice ? "h-v-55" : "h-v-41"}`}>
                    <Grid item xs={12} className={`${props.IsMobileDevice ? "" : ""}`}>
                        <div className={`p-relative ${!props.IsMobileDevice ? '' : ""}`}>
                            <Grid item xs={12} className={`mt-1 ${props.IsMobileDevice ? "mb-2" : "mb-2"}`}>
                                <TextField autoFocus
                                    placeholder="Group Name"
                                    label="Group Name"
                                    name="NewGroupName"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    required
                                    inputProps={{ tabIndex: 1, "data-arrow-index": 500 }}
                                    value={NewGroupName}
                                    InputProps={{ classes: { input: `${props.IsMobileDevice ? 'h2' : 'h5-regular'} color-text-field-font` } }}
                                    onChange={(e) =>
                                        editGroup(e.target.name, e.target.value)}
                                />
                                <FormHelperText className="h6-regular color-primary-danger t-align-right">
                                    {errors.NewGroupName}
                                </FormHelperText>
                            </Grid>
                        </div>
                        <Grid
                            item
                            xs={12}
                            sm={12}
                            md={8}
                            lg={8}
                            xl={8}
                            className={`${!props.IsMobileDevice ? " mb-1" : "w-fill"
                                } d-flex c-pointer py-search`}
                            style={{ zIndex: 10, position: 'relative' }}
                        >
                            <TextField
                                inputProps={{
                                    tabIndex: 4,
                                    "data-arrow-index": 200,
                                }}
                                fullWidth
                                placeholder={"Find a contact..."}
                                onChange={searchContacts}
                                variant="outlined"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <GridSearchIcon />
                                        </InputAdornment>
                                    ),
                                    classes: {
                                        input: "h6-regular color-text-field-font max-fit-h-v-1",
                                        notchedOutline: "br-4",
                                    },
                                }}
                                size="small"
                                className="my-auto"
                            />
                        </Grid>
                    </Grid>
                    {
                        !!ContactsInGroup?.length &&
                        <div className="color-display-content p-absolute h5-semibold pl-2">
                            {UtilityService.maskSingleDigitWithZero(ContactsInGroup?.length)}
                            <span className="color-plans-details-text  h5-regular"> {ContactsInGroup?.length === 1 ? "contact selected" : "contacts selected"}</span>
                        </div>
                    }
                    {
                        IsMobileDevice && <div className={"mb-2"} />
                    }
                    <GridComponent
                        ColumnDefs={EditGroupColoumnDefs}
                        RowData={data}
                        OnGridReady={onGridReady}
                        OnSelectionChanged={onSelectionChanged}
                        IsPaginationEnabled={false}
                        EmptyLabel={emptyLabels.CONTACTS}
                        LockVisible={true}
                    />
                </form>
            </DialogContent >
            <DialogActions className={`px-3 ${IsDarkMode ? " bg-color-sidebar-bg" : ""}`}>
                <ButtonComponent OnClick={() => props.closeHandler(false)} buttonProps={{ tabIndex: 8 }} ButtonLabel={buttonLabels.CANCEL} ClassName={`mb-1 ml-auto ${IsTeamsApp ? " mb-1" : ""}`} Variant={"secondary-btn-small"} />
                <ButtonComponent buttonProps={{ tabIndex: 7 }} OnClick={saveGroup} Type="submit" Loading={IsLoading} ButtonLabel={buttonLabels.SAVE} ClassName={`px-4 mb-1 ${IsTeamsApp ? " mb-1" : ""} ${!props.IsMobileDevice ? '' : 'mr-2'}`} Variant={errors.NewGroupName !== "" ? "disabled-btn-small" : "primary-btn-small"} />
            </DialogActions>
        </>
    )
}

export default EditGroupComponent;